// Seo.tsx
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { DEEP_LINK_BASE_URL, ANDROID_PACKAGE, IOS_APP_ID, IOS_BUNDLE_ID } from "../utils/deepLinkConfig";

interface MetaData {
  title: string;
  description: string;
  keywords: string;
}

const seoData: Record<string, MetaData> = {
  "/": {
    title: "Amaar Marketplace | Onlayn Savdo Platformasi – Tovarlar, Aksiyalar, Yetkazib berish",
    description:
      "Amaar – innovatsion onlayn bozor (marketplace) O‘zbekistonda. Aksiyalar, keng assortiment, qulay yetkazib berish. Hozir xarid qil!",
    keywords: "Amaar, marketplace, O'zbekiston, onlayn bozor, aksiyalar, yetkazib berish",
  },
  "/uz": {
    title: "Amaar Marketplace | O‘zbekiston boʻyicha onlayn bozor",
    description:
      "Amaar Marketplace – Oʻzbekistonda keng assortiment va qulay xarid qilish imkoniyati bilan yetakchi onlayn bozor. Aksiya va aksessuarlar bilan!",
    keywords: "Amaar, onlayn bozor, O‘zbekiston, elektron savdo, chegirmalar, yetkazib berish",
  },
  "/uz/categories": {
    title: "Toifalar | Amaar Marketplace",
    description: "Amaar bo‘limlari: elektronika, kiyim-kechak, go‘zallik, maishiy texnika va boshqa mahsulotlar.",
    keywords: "Amaar bo‘lim, toifalar, elektronik, kiyim, go‘zallik, maishiy texnika",
  },
  "/uz/contact": {
    title: "Bog‘lanish | Amaar Marketplace",
    description:
      "Amaar bilan bog‘laning: savollar, yordam, biznes takliflar uchun aloqa ma’lumotlari va mijozlarni qo‘llab-quvvatlash.",
    keywords: "aloqa, yordam, Amaar, mijozlarni qo‘llab-quvvatlash, kontakt",
  },
  "/uz/faq": {
    title: "FAQ | Amaar Marketplace – Savollar va Javoblar",
    description: "Tez-tez so‘raladigan savollar (FAQ) – xarid, yetkazib berish, to‘lov va qaytarish haqida ma’lumotlar.",
    keywords: "FAQ, tez-tez beriladigan savollar, yetkazib berish, to‘lov, qaytarish",
  },
};

const defaultMeta: MetaData = {
  title: "Amaar Marketplace | Onlayn Bozor – Keng Assortiment va Tez Yetkazib berish",
  description:
    "Amaar – zamonaviy onlayn bozor O‘zbekiston bo‘yicha. Aksiyalar, keng assortiment va qulay yetkazib berish.",
  keywords: "Amaar, onlayn bozor, O‘zbekiston, elektron savdo, tez yetkazib berish",
};

const Seo: React.FC = () => {
  const { pathname } = useLocation();
  const current = seoData[pathname] || defaultMeta;

  useEffect(() => {
    document.title = current.title;

    const setMeta = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    const setOg = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      let tag = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute("href", href);
    };

    // Standard meta
    setMeta("description", current.description);
    setMeta("keywords", current.keywords);
    setMeta("author", "Amaar Marketplace");
    setMeta("robots", "index, follow");
    setMeta("viewport", "width=device-width, initial-scale=1");

    // Open Graph
    setOg("og:title", current.title);
    setOg("og:description", current.description);
    setOg("og:type", "website");
    setOg("og:url", `https://amaar.uz${pathname}`);
    setOg("og:image", "https://amaar.uz/og-image.jpg");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", current.title);
    setMeta("twitter:description", current.description);
    setMeta("twitter:image", "/public/amaar.webp");

    // Twitter App Card — enables "Open in App" from Twitter/X
    setMeta("twitter:app:name:iphone", "AMAAR");
    setMeta("twitter:app:id:iphone", IOS_APP_ID);
    setMeta("twitter:app:url:iphone", `amaar:/${pathname}`);
    setMeta("twitter:app:name:googleplay", "AMAAR");
    setMeta("twitter:app:id:googleplay", ANDROID_PACKAGE);
    setMeta("twitter:app:url:googleplay", `amaar:/${pathname}`);

    // Dynamic App Links (per-page deep link URLs for social sharing)
    setOg("al:ios:url", `amaar:/${pathname}`);
    setOg("al:ios:app_store_id", IOS_APP_ID);
    setOg("al:ios:app_name", "AMAAR");
    setOg("al:android:url", `amaar:/${pathname}`);
    setOg("al:android:package", ANDROID_PACKAGE);
    setOg("al:android:app_name", "AMAAR");
    setOg("al:web:url", `${DEEP_LINK_BASE_URL}${pathname}`);
    setOg("al:web:should_fallback", "true");

    // Smart App Banner — dynamically update app-argument with current page
    const smartBanner = document.querySelector('meta[name="apple-itunes-app"]') as HTMLMetaElement;
    if (smartBanner) {
      smartBanner.setAttribute("content", `app-id=${IOS_APP_ID}, app-argument=https://amaar.uz${pathname}`);
    }

    // Canonical
    setLink("canonical", `https://amaar.uz${pathname}`);

    // Favicon
    setLink("icon", "/public/amaar.webp");
  }, [pathname, current]);

  return null;
};

export default Seo;
